import React from 'react';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { StatementHeader } from './ReportHelpers';

export function TrialBalanceReport({ reportData, viewMode, entityName }) {
  if (!reportData) return null;
  const accounts = reportData.accounts || [];
  const isBalanced = reportData.is_balanced !== false;

  if (viewMode === 'list') {
    return (
      <Card data-testid="tb-list-view">
        <CardContent className="py-8 px-6 max-w-3xl mx-auto">
          <StatementHeader entityName={entityName} title="Trial Balance" asOf={reportData.as_of_date} />
          <table className="w-full text-sm">
            <thead>
              <tr className="border-y-2 border-zinc-800 bg-zinc-50/50">
                <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-zinc-600">Code</th>
                <th className="py-2 px-4 text-left text-xs font-semibold uppercase tracking-wider text-zinc-600">Account</th>
                <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600">Debit</th>
                <th className="py-2 px-4 text-right text-xs font-semibold uppercase tracking-wider text-zinc-600 pr-6">Credit</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.account_code} className="border-b border-zinc-200 hover:bg-zinc-50/50">
                  <td className="py-2 px-4 font-mono text-sm text-zinc-400">{a.account_code}</td>
                  <td className="py-2 px-4 text-sm text-zinc-700">{a.account_name}</td>
                  <td className="py-2 px-4 text-right font-mono text-sm tabular-nums">{a.debit > 0 ? formatCurrency(a.debit) : '-'}</td>
                  <td className="py-2 px-4 text-right font-mono text-sm tabular-nums pr-6">{a.credit > 0 ? formatCurrency(a.credit) : '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-zinc-900 border-b-4 border-double border-zinc-900 bg-zinc-100">
                <td colSpan="2" className="py-2 px-4 font-semibold text-sm">TOTAL</td>
                <td className="py-2 px-4 text-right font-mono text-sm tabular-nums font-semibold">{formatCurrency(reportData.total_debit)}</td>
                <td className="py-2 px-4 text-right font-mono text-sm tabular-nums font-semibold pr-6">{formatCurrency(reportData.total_credit)}</td>
              </tr>
            </tfoot>
          </table>
          <div className={`mt-4 text-center text-xs font-semibold ${isBalanced ? 'text-emerald-600' : 'text-red-600'}`}>
            {isBalanced ? 'Debits = Credits' : 'WARNING: Trial balance does not balance'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="text-lg">Trial Balance</CardTitle><p className="text-sm text-zinc-500">As of {formatDate(reportData.as_of_date)}</p></div>
        <Badge className={isBalanced ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>{isBalanced ? 'Balanced' : 'Not Balanced'}</Badge>
      </CardHeader>
      <CardContent className="p-0">
        <table className="report-table" data-testid="trial-balance-table">
          <thead><tr><th className="pl-4">Code</th><th>Account</th><th>Type</th><th className="text-right">Debit</th><th className="text-right pr-4">Credit</th></tr></thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.account_code}><td className="font-mono text-zinc-400 pl-4">{a.account_code}</td><td>{a.account_name}</td><td className="capitalize text-zinc-500 text-sm">{a.account_type}</td><td className="text-right font-mono">{a.debit > 0 ? formatCurrency(a.debit) : '-'}</td><td className="text-right font-mono pr-4">{a.credit > 0 ? formatCurrency(a.credit) : '-'}</td></tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t-2 border-zinc-300"><td colSpan="3" className="font-semibold pl-4">Total</td><td className="text-right font-mono font-semibold">{formatCurrency(reportData.total_debit)}</td><td className="text-right font-mono font-semibold pr-4">{formatCurrency(reportData.total_credit)}</td></tr></tfoot>
        </table>
      </CardContent>
    </Card>
  );
}
